import { useCallback, useEffect, useState } from 'react';
import { isInTelegram, alertDialog } from '../telegram';
import { createDepositAct, downloadDepositAct, getDepositAct, getDepositActs, sendDepositActLink } from '../api';
import type { DepositAct } from '../types';

// ============================================================================
// Блок F (депозиты): «Акт сверки» на странице «Депозит» (docs/deposits-spec.md раздел 15).
// Только по запросу: клиент выбирает период, акт получает номер АС-…, PDF скачивается
// в браузере, в Telegram и на e-mail уходит только временная ссылка (30 дней).
// ============================================================================

interface Props {
  legalEntityId: string;
  legalEntityName: string | null;
}

const fmtPrice = (n: number | string) =>
  Number(n).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function presetRange(kind: 'prev' | 'current' | 'all'): [string, string] {
  const now = new Date();
  if (kind === 'current') return [isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), isoDate(now)];
  if (kind === 'prev') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return [isoDate(first), isoDate(last)];
  }
  return ['2026-01-01', isoDate(now)];
}

const btn: React.CSSProperties = {
  padding: '8px 10px', border: 'none', borderRadius: 10, background: 'var(--tg-secondary-bg)',
  color: 'var(--tg-button)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};
const btnPrimary: React.CSSProperties = {
  ...btn, width: '100%', padding: '11px', background: 'var(--tg-button)', color: 'var(--tg-button-text, #fff)', fontSize: 14, fontWeight: 700,
};
const inputStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 10, fontSize: 14, fontFamily: 'inherit',
  border: '1px solid var(--tg-secondary-bg)', background: 'var(--tg-bg)', color: 'var(--tg-text)',
};

export default function ActsSection({ legalEntityId, legalEntityName }: Props) {
  const [range, setRange] = useState<[string, string]>(() => presetRange('prev'));
  const [acts, setActs] = useState<DepositAct[]>([]);
  const [current, setCurrent] = useState<DepositAct | null>(null);
  const [showRows, setShowRows] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const loadActs = useCallback(() => {
    getDepositActs(legalEntityId)
      .then(({ data }) => setActs(data))
      .catch(() => { /* список не критичен */ });
  }, [legalEntityId]);

  useEffect(() => { setCurrent(null); setShowRows(false); loadActs(); }, [loadActs]);

  const create = async () => {
    if (creating) return;
    if (range[0] > range[1]) { alertDialog('Дата начала периода позже даты окончания.'); return; }
    setCreating(true);
    try {
      const { data } = await createDepositAct({ legal_entity_id: legalEntityId, period_from: range[0], period_to: range[1] });
      setCurrent(data);
      setShowRows(false);
      loadActs();
    } catch (e) {
      alertDialog(`Не удалось сформировать акт: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  };

  const open = async (act: DepositAct) => {
    setBusy(`${act.id}:open`);
    try {
      const { data } = await getDepositAct(act.id);
      setCurrent(data);
      setShowRows(true);
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const send = async (act: DepositAct, channel: 'telegram' | 'email') => {
    if (busy) return;
    setBusy(`${act.id}:${channel}`);
    try {
      await sendDepositActLink(act.id, channel);
      alertDialog(channel === 'telegram'
        ? `Ссылка на акт ${act.number} отправлена в чат с ботом.`
        : `Ссылка на акт ${act.number} отправлена на ваш e-mail.`);
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const download = async (act: DepositAct) => {
    if (busy) return;
    setBusy(`${act.id}:pdf`);
    try {
      await downloadDepositAct(act.id, act.number);
    } catch (e) {
      alertDialog((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const actions = (act: DepositAct) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
      {isInTelegram() ? (
        <button onClick={() => send(act, 'telegram')} disabled={!!busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
          {busy === `${act.id}:telegram` ? 'Отправляем…' : '📩 Ссылку в чат'}
        </button>
      ) : (
        <button onClick={() => download(act)} disabled={!!busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
          {busy === `${act.id}:pdf` ? 'Загрузка…' : '📄 Скачать PDF'}
        </button>
      )}
      <button onClick={() => send(act, 'email')} disabled={!!busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
        {busy === `${act.id}:email` ? 'Отправляем…' : '✉️ На e-mail'}
      </button>
    </div>
  );

  const data = current?.data;

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 15 }}>Акт сверки по депозиту</h4>
      <p className="hint" style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.4 }}>
        {legalEntityName ? `${legalEntityName}. ` : ''}
        Акт формируется только по вашему запросу. Настоящий Акт сверки не является бухгалтерским документом
        и не имеет юридической силы; для получения акта, имеющего юридическую силу, свяжитесь с менеджером поставщика.
      </p>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        <button style={btn} onClick={() => setRange(presetRange('prev'))}>Прошлый месяц</button>
        <button style={btn} onClick={() => setRange(presetRange('current'))}>Текущий месяц</button>
        <button style={btn} onClick={() => setRange(presetRange('all'))}>С начала работы</button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
        <input type="date" value={range[0]} onChange={e => setRange([e.target.value, range[1]])} style={inputStyle} />
        <span className="hint">—</span>
        <input type="date" value={range[1]} onChange={e => setRange([range[0], e.target.value])} style={inputStyle} />
      </div>
      <button onClick={create} disabled={creating} style={{ ...btnPrimary, opacity: creating ? 0.6 : 1 }}>
        {creating ? 'Формируем…' : '📑 Сформировать акт'}
      </button>

      {current && (
        <div className="card" style={{ marginTop: 12, marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontWeight: 700 }}>{current.number}</span>
            <span className="hint">{fmtDate(current.period_from)} – {fmtDate(current.period_to)}</span>
          </div>
          <div style={{ fontSize: 13, marginTop: 6, display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 2 }}>
            <span className="hint">Сальдо на начало</span><span>{fmtPrice(current.opening_balance)}</span>
            <span className="hint">Пополнения</span><span>+{fmtPrice(current.total_in)}</span>
            <span className="hint">Списания за отгрузки</span><span>−{fmtPrice(current.total_out)}</span>
            <span className="hint">Возвраты по претензиям</span><span>+{fmtPrice(current.total_returns)}</span>
            <span className="hint">Корректировки и возвраты средств</span><span>{fmtPrice(current.total_adjustments)}</span>
            <span style={{ fontWeight: 700 }}>Сальдо на конец</span><span style={{ fontWeight: 700, color: 'var(--tg-button)' }}>{fmtPrice(current.closing_balance)}</span>
          </div>
          {actions(current)}
          {data && data.sections.some(s => s.rows.length > 0) && (
            <button onClick={() => setShowRows(v => !v)} style={{ ...btn, marginTop: 8, background: 'transparent', padding: '4px 0' }}>
              {showRows ? 'Скрыть операции ▴' : 'Показать операции ▾'}
            </button>
          )}
          {showRows && data && data.sections.map(sec => (
            <div key={sec.coffee_shop_id} style={{ marginTop: 8 }}>
              {data.sections.length > 1 && <div style={{ fontWeight: 600, fontSize: 13 }}>{sec.coffee_shop_name}</div>}
              {sec.rows.length === 0 && <div className="hint" style={{ fontSize: 12 }}>Операций за период нет.</div>}
              {sec.rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderTop: '1px solid var(--tg-bg)', fontSize: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="hint">{fmtDate(r.date)}</div>
                    <div>{r.description}</div>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 700, color: r.credit ? '#34c759' : '#ff3b30' }}>{r.credit ? `+${fmtPrice(r.credit)}` : `−${fmtPrice(r.debit ?? 0)}`}</div>
                    <div className="hint">{fmtPrice(r.balance)}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {acts.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="hint" style={{ fontSize: 12, marginBottom: 4 }}>Ранее сформированные акты</div>
          {acts.slice(0, 10).map(act => (
            <div key={act.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--tg-secondary-bg)', fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{act.number}</div>
                <div className="hint" style={{ fontSize: 12 }}>{fmtDate(act.period_from)} – {fmtDate(act.period_to)} · сальдо {fmtPrice(act.closing_balance)}</div>
              </div>
              <button onClick={() => open(act)} disabled={!!busy} style={btn}>
                {busy === `${act.id}:open` ? '…' : 'Открыть'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
